/**
 * Compare Project Customer IDs vs Mapping IDs
 * This script shows exactly which customer IDs are in projects vs mappings
 */

import { directSupabase } from '../lib/supabaseClient';

export async function compareProjectMappingIds() {
  console.log('🔍 Comparing project customer IDs vs mapping IDs...');
  
  try {
    // Step 1: Get all project customer_id values
    console.log('📊 Fetching project customer IDs...');
    const { data: projects, error: projectError } = await directSupabase
      .from('projects')
      .select('id, project_name, customer_id')
      .not('customer_id', 'is', null);
    
    if (projectError) {
      throw new Error(`Failed to fetch projects: ${projectError.message}`);
    }
    
    const projectCustomerIds = [...new Set(projects?.map(p => p.customer_id).filter(Boolean) || [])];
    console.log(`Found ${projects?.length || 0} projects with ${projectCustomerIds.length} unique customer IDs`);
    
    // Step 2: Get all mapping quotedock_customer_id values
    console.log('📊 Fetching mapping customer IDs...');
    const { data: mappings, error: mappingError } = await directSupabase
      .from('customer_migration_mapping')
      .select('quotedock_customer_id, jarvis_customer_id, customer_name');
    
    if (mappingError) {
      throw new Error(`Failed to fetch mappings: ${mappingError.message}`);
    }
    
    const mappingQuoteDockIds = mappings?.map(m => m.quotedock_customer_id) || [];
    console.log(`Found ${mappings?.length || 0} mappings with QuoteDock IDs`);
    
    // Step 3: Compare the ID sets
    console.log('🔍 Comparing ID sets...');
    
    // Convert project IDs to numbers for comparison (they might be stored as strings)
    const projectIds = projectCustomerIds.map(id => {
      const num = Number(id);
      return isNaN(num) ? id : num;
    });
    
    const matches: any[] = [];
    const projectOnlyIds: any[] = [];
    const mappingOnlyIds: number[] = [];
    
    // Find matches
    for (const projectId of projectIds) {
      if (mappingQuoteDockIds.includes(projectId)) {
        const mapping = mappings?.find(m => m.quotedock_customer_id === projectId);
        matches.push({
          customer_id: projectId,
          customer_name: mapping?.customer_name,
          jarvis_uuid: mapping?.jarvis_customer_id,
          project_count: projects?.filter(p => Number(p.customer_id) === projectId || p.customer_id === projectId).length
        });
      } else {
        projectOnlyIds.push(projectId);
      }
    }
    
    // Find mapping-only IDs (not in any projects)
    for (const mappingId of mappingQuoteDockIds) {
      if (!projectIds.includes(mappingId)) {
        mappingOnlyIds.push(mappingId);
      }
    }
    
    // Step 4: Show detailed comparison
    console.log('📊 ID Comparison Results:');
    console.log(`   - Project customer IDs: ${projectIds.length} unique`);
    console.log(`   - Mapping QuoteDock IDs: ${mappingQuoteDockIds.length} unique`);
    console.log(`   - Matches found: ${matches.length}`);
    console.log(`   - Project-only IDs: ${projectOnlyIds.length}`);
    console.log(`   - Mapping-only IDs: ${mappingOnlyIds.length}`);
    
    if (matches.length > 0) {
      console.log('🎯 Matching customer IDs (these should be updated):');
      console.table(matches);
    }
    
    if (projectOnlyIds.length > 0) {
      console.log('❌ Project customer IDs NOT in mappings:');
      console.table(projectOnlyIds.map(id => ({ customer_id: id, type: typeof id })));
      
      // Show sample projects with these IDs
      const sampleProjects = projects?.filter(p => projectOnlyIds.includes(Number(p.customer_id) || p.customer_id)).slice(0, 10);
      if (sampleProjects && sampleProjects.length > 0) {
        console.log('📋 Sample projects with unmapped customer IDs:');
        console.table(sampleProjects);
      }
    }
    
    if (mappingOnlyIds.length > 0 && mappingOnlyIds.length <= 20) {
      console.log('ℹ️ Mapping QuoteDock IDs NOT used by any projects (sample):');
      const sampleMappingOnlyIds = mappingOnlyIds.slice(0, 10);
      const sampleMappings = mappings?.filter(m => sampleMappingOnlyIds.includes(m.quotedock_customer_id));
      console.table(sampleMappings);
    }
    
    // Step 5: Type analysis
    console.log('🔍 Data type analysis:');
    const projectIdTypes = projectIds.map(id => ({ id, type: typeof id, isNumber: !isNaN(Number(id)) }));
    const typeAnalysis = {
      string: projectIdTypes.filter(t => t.type === 'string').length,
      number: projectIdTypes.filter(t => t.type === 'number').length,
      convertible_to_number: projectIdTypes.filter(t => t.isNumber).length
    };
    console.table(typeAnalysis);
    
    return {
      success: true,
      projectCount: projects?.length || 0,
      uniqueProjectIds: projectIds.length,
      mappingCount: mappings?.length || 0,
      matches: matches.length,
      projectOnlyIds: projectOnlyIds.length,
      mappingOnlyIds: mappingOnlyIds.length,
      matchDetails: matches,
      unmappedProjectIds: projectOnlyIds,
      typeAnalysis
    };
    
  } catch (error) {
    console.error('❌ ID comparison failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to run from browser console
(window as any).compareIds = compareProjectMappingIds;
